// src/components/Contact/Contact.js
import React, { useState, useEffect, useRef } from 'react';
import { Button, View, Heading, TextField } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import './Contact.css';
import { publishToSNS } from '../../utils/snsPublisher';
import LocationSearch from '../LocationSearch/LocationSearch';
import GroupSizeField from './GroupSizeField';
import ContactMethodField from './ContactMethodField';
import ContactField from './ContactField';
import { formatPhoneNumber, validateContact } from '../../utils/utils';

const Contact = () => {
  const [name, setName] = useState('');
  const [groupSize, setGroupSize] = useState('1');
  const [contactMethod, setContactMethod] = useState('email');
  const [contact, setContact] = useState('');
  const [formattedContact, setFormattedContact] = useState('');
  const [location, setLocation] = useState('');
  const [isLocationConfirmed, setIsLocationConfirmed] = useState(false);
  const [error, setError] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [HowDidYouHearAboutUs, setHowDidYouHearAboutUs] = useState('');
  const [workshopAttendanceIntent, setWorkshopAttendanceIntent] = useState('');
  const [activeModal, setActiveModal] = useState(null);
  const [consents, setConsents] = useState({
    c1: false,
    c2: false,
    c3: false,
  });

  // Individual field validation states
  const [isNameValid, setIsNameValid] = useState(true);
  const [isLocationValid, setIsLocationValid] = useState(true);
  const [isContactValid, setIsContactValid] = useState(true);

  // Consent validation helpers
  const areConsentsRequired = contactMethod !== 'no-contact';
  const areConsentsValid = !areConsentsRequired || (consents.c1 && consents.c2);

  // 🔹 NEW: How-did-you-hear validation
  const isHowDidYouHearValid = HowDidYouHearAboutUs !== '';
  const isWorkshopAttendanceIntentValid = workshopAttendanceIntent !== '';

  const openModal = (modalName) => setActiveModal(modalName);
  const closeModal = () => setActiveModal(null);

  useEffect(() => {
    const hasName = name.trim() !== '';
    const isValid =
      contactMethod === 'no-contact'
        ? hasName && location !== ''
        : hasName &&
          location !== '' &&
          isLocationConfirmed &&
          validateContact(contactMethod, contact);

    setIsFormValid(isValid);

    // Update individual validation states
    setIsNameValid(hasName);
    setIsLocationValid(location !== '' && isLocationConfirmed);
    setIsContactValid(
      contactMethod === 'no-contact' ||
        validateContact(contactMethod, contact)
    );
  }, [name, groupSize, contactMethod, contact, location, isLocationConfirmed]);
  useEffect(() => {
      if (contactMethod === 'no-contact') {
        setConsents({
          c1: false,
          c2: false,
          c3: false,
        });
      }
    }, [contactMethod]);

  useEffect(() => {
    document.body.style.overflow = activeModal ? 'hidden' : '';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeModal]);

  const handleLocationSelect = (placeName) => {
    setLocation(placeName);
    setIsLocationConfirmed(true);
  };

  const handleContactChange = (e) => {
    const value = e.target.value;
    if (contactMethod === 'phone' || contactMethod === 'text') {
      const formattedValue = formatPhoneNumber(value);
      setFormattedContact(formattedValue);
      setContact(value.replace(/[^\d]/g, ''));
    } else {
      setFormattedContact(value);
      setContact(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // React state updates are asynchronous, so use a ref to synchronously
    // prevent rapid clicks or multiple submit events from sending twice.
    if (isSubmittingRef.current) {
      return;
    }

    if (
      !isFormValid ||
      !isHowDidYouHearValid ||
      !isWorkshopAttendanceIntentValid
    ) {
      setError('Please fill out all required fields.');
      return;
    }

    setError('');

    if (!areConsentsValid) {
      setError('All consents are required.');
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    const message = {
      name: name.trim(),
      groupSize,
      contactMethod,
      contact,
      location,
      HowDidYouHearAboutUs,
      workshopAttendanceIntent,
      consentPSEPNotifications:
        contactMethod === 'no-contact' ? false : consents.c1,
      consentTitleAlert:
        contactMethod === 'no-contact' ? false : consents.c2,
    };

    try {
      await publishToSNS(message);
      setIsSubmitted(true);

      // Refresh the page after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (submitError) {
      console.error('Form submission failed:', submitError);
      setError('Submission failed. Please try again.');
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const errors = [];

  if (!isNameValid) errors.push('Name Required');
  if (!isLocationValid) errors.push('Address Required');
  if (!isContactValid) errors.push('Contact Required');
  if (!isHowDidYouHearValid) errors.push('How did you hear about us Required');
  if (!isWorkshopAttendanceIntentValid) errors.push('Workshop attendance response Required');
  if (!areConsentsValid) errors.push('Required consents are missing');

  return (
    <View className="form-container">
      <div className="letterhead">
        <img
          src={`${process.env.PUBLIC_URL}/letterhead.png`}
          alt="Alachua County Property Appraiser letterhead"
        />
      </div>

      <div className="form-body">
        <section className="event-banner" aria-labelledby="event-title">
          <div className="event-banner-heading">
            <span className="event-kicker">Free Community Event</span>
            <h1 id="event-title">10th Probate and Estate Planning Summit</h1>
            <p>
              Join the Alachua County Property Appraiser&apos;s Office for our
              free 10th Probate and Estate Planning Summit. Learn about
              heirs&apos; property, probate, estate planning, and protecting
              your family&apos;s property.
            </p>
          </div>

          <div className="event-details">
            <h2>Event Details</h2>
            <dl className="event-details-grid">
              <div className="event-detail">
                <dt>Date</dt>
                <dd>Thursday, August 13, 2026</dd>
              </div>
              <div className="event-detail">
                <dt>Time</dt>
                <dd>
                  Check-In: 5:00 p.m.
                  <br />
                  Program Begins: 6:00 p.m.
                </dd>
              </div>
              <div className="event-detail">
                <dt>Location</dt>
                <dd>
                  Greater Bethel A.M.E. Church
                  <br />
                  701 SE 43rd Street
                  <br />
                  Gainesville, FL 32641
                </dd>
              </div>
            </dl>
          </div>
        </section>

        <Heading level={2} className="form-title">
          Probate & Estate Summit
        </Heading>

        <div className="form-intro">
          <h3>What is the Summit?</h3>
          <p>
            The Probate & Estate Summit is a free community event hosted by the
            Alachua County Property Appraiser’s Office to help property owners
            understand the probate process, estate planning resources, and ways
            to protect their property rights.
          </p>
          <h3>What is Title Alert?</h3>
          <p>
            Title Alert is a free service provided by the Alachua County Property
            Appraiser’s Office that allows residents to monitor changes in
            property ownership. Once registered, subscribers will receive
            automated notifications if there are any ownership changes on the
            property they’re monitoring.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className={`form-field grid-field ${!isNameValid ? 'invalid' : ''}`}>
              <label className="amplify-label">
                Name <span className="required-star">*</span>
              </label>
              <TextField
                label=""
                labelHidden
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                variation="quiet"
              />
            </div>
          </div>

          <div className="form-row">
            <div
              className={`form-field grid-field location-grid ${
                !isLocationValid ? 'invalid' : ''
              }`}
            >
              <label className="amplify-label address-title" htmlFor="location-search">
                Address <span className="required-star">*</span>
              </label>

              <div id="location-search">
                <LocationSearch
                  onResultSelect={handleLocationSelect}
                  setIsLocationConfirmed={setIsLocationConfirmed}
                />
              </div>
            </div>
          </div>

          <div className="form-row form-row-wide-right">
            <ContactMethodField
              value={contactMethod}
              onChange={(e) => setContactMethod(e.target.value)}
            />

            {contactMethod !== 'no-contact' && (
              <ContactField
                value={formattedContact}
                onChange={handleContactChange}
                className={`${!isContactValid ? 'invalid' : ''}`}
              />
            )}
          </div>

          <div className="form-row form-row-wide-right">
            <GroupSizeField
              value={groupSize}
              onChange={(e) => setGroupSize(e.target.value)}
            />

            <div className="form-field grid-field">
              <label className="amplify-label">
                How did you hear about us? <span className="required-star">*</span>
              </label>

              <div className="amplify-select__wrapper">
                <select
                  className="amplify-select amplify-field-group__control amplify-select--quiet"
                  value={HowDidYouHearAboutUs}
                  onChange={(e) => setHowDidYouHearAboutUs(e.target.value)}
                  required
                >
                  <option value="">Select one</option>
                  <option value="mailer">Mailer</option>
                  <option value="social-media">Social Media</option>
                  <option value="email">Email</option>
                  <option value="radio">Radio</option>
                  <option value="word-of-mouth">Word of Mouth</option>
                </select>

                <div
                  className="amplify-flex amplify-select__icon"
                  aria-hidden="true"
                >
                  <span className="amplify-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24">
                      <path d="M16.59 8.59L12 13.17L7.41 8.59L6 10l6 6 6-6z" />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div
            className="section-box"
            role="group"
            aria-labelledby="attend-workshop-heading"
          >
            <div className="section-header" id="attend-workshop-heading">
              Attend Workshop
            </div>
            <div className="section-content">
              <div className="section-lede">
                Would you like to attend the free workshop on September 26 from
                9:00 AM to 4:00 PM at the same location as the Summit?{' '}
                <button
                  className="modal-link"
                  type="button"
                  onClick={() => openModal('summit')}
                >
                  Learn more about the Workshop.
                </button>
              </div>

              <div className="radio-group">
                <label>
                  <input
                    type="radio"
                    name="workshop-attendance-intent"
                    checked={workshopAttendanceIntent === 'yes'}
                    onChange={(e) =>
                      setWorkshopAttendanceIntent(e.target.checked ? 'yes' : '')
                    }
                    required
                  />
                  Yes
                </label>

                <label>
                  <input
                    type="radio"
                    name="workshop-attendance-intent"
                    checked={workshopAttendanceIntent === 'no'}
                    onChange={(e) =>
                      setWorkshopAttendanceIntent(e.target.checked ? 'no' : '')
                    }
                    required
                  />
                  No
                </label>
              </div>
            </div>
          </div>

          {contactMethod !== 'no-contact' && (
            <div
              className="section-box"
              role="group"
              aria-labelledby="consents-heading"
            >
              <div className="section-header" id="consents-heading">
                Consents & Acknowledgements
              </div>
              <div className="section-content">
                <div className="consent-text">
                  The Alachua County Property Appraiser’s Office (ACPA) provides
                  property data for tax purposes but does not guarantee its
                  accuracy. Official printed documents take precedence. This is not
                  a substitute for a title search. Ownership records come from the
                  Clerk of Court’s Office. ACPA cannot verify deed validity or
                  settle ownership disputes. Suspected fraud should be reported to
                  law enforcement.
                </div>

                <div className="checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={consents.c2}
                      onChange={(e) =>
                        setConsents((prev) => ({
                          ...prev,
                          c2: e.target.checked,
                        }))
                      }
                    />
                    <span>
                      (Required) I have reviewed and acknowledge the Title Alert
                      Terms and Conditions.{' '}
                      <button
                        className="modal-link"
                        type="button"
                        onClick={() => openModal('titlealert')}
                      >
                        View Terms
                      </button>
                    </span>
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      checked={consents.c3}
                      onChange={(e) =>
                        setConsents((prev) => ({
                          ...prev,
                          c3: e.target.checked,
                        }))
                      }
                    />
                    <span>
                      I certify that I am already enrolled in the Title Alert
                      service.
                    </span>
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      checked={consents.c1}
                      onChange={(e) =>
                        setConsents((prev) => ({
                          ...prev,
                          c1: e.target.checked,
                        }))
                      }
                    />
                    <span>
                      (Required) I consent to being contacted by the Alachua County
                      Property Appraiser's Office regarding probate- and estate
                      planning-related programs, resources, services, and
                      initiatives.
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          <Button
            type="submit"
            className="submit-button"
            variation="primary"
            disabled={
              isSubmitting ||
              !isFormValid ||
              !isHowDidYouHearValid ||
              !isWorkshopAttendanceIntentValid ||
              !areConsentsValid
            }
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>

          {error && <p className="error-message">{error}</p>}

          {errors.length > 0 && (
            <div className="error-strip">
              {errors.map((msg, i) => (
                <span key={i}>*{msg}</span>
              ))}
            </div>
          )}

          {isSubmitted && (
            <p className="confirmation-message">
              Your message has been successfully submitted!
            </p>
          )}
        </form>
      </div>

      <div
        className={`modal-backdrop ${activeModal === 'summit' ? 'open' : ''}`}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) closeModal();
        }}
      >
        <div className="modal-box" role="dialog" aria-modal="true">
          <div className="modal-letterhead">
            <img
              src={`${process.env.PUBLIC_URL}/letterhead.png`}
              alt="Alachua County Property Appraiser letterhead"
            />
            <button
              className="modal-close"
              type="button"
              onClick={closeModal}
              aria-label="Close Summit information"
            >
              x
            </button>
          </div>
          <div className="modal-body">
            <h3>Learn more about the Workshop</h3>
            <p>
              After attending a summit, the next step is to attend one of our
              hands-on workshops, where you’ll receive personalized guidance on
              how to move forward.
            </p>
            <p>You will gain:</p>
            <ul>
              <li>
                Insights into completing a family tree and documenting heirs
              </li>
              <li>
                Support with gathering documents relevant to your estate or
                probate case
              </li>
              <li>
                A better understanding of what you need before hiring an attorney
              </li>
              <li>
                Access to legal Q&As, property information, and tax guidance
              </li>
            </ul>
            <p>
              <strong>Note:</strong> Depending on the complexity of your case,
              you may not complete your documents during the workshop—but you
              will leave with clear direction on what’s next.
            </p>
            <h3>Date & Location</h3>
            <p>
              <strong>Date:</strong> September 26
              <br />
              <strong>Time:</strong> 9:00 AM to 4:00 PM
              <br />
              <strong>Location:</strong> Same location as the Summit
            </p>
            <h3>What to Expect</h3>
            <ul>
              <li>Probate and estate planning information for property owners</li>
              <li>Title Alert enrollment and fraud prevention resources</li>
              <li>Opportunities to ask questions and connect with ACPA staff</li>
              <li>Free printed resources and community guidance</li>
            </ul>
            <h3>Is There a Cost?</h3>
            <p>No. The Summit and workshop are free to attend.</p>
          </div>
          <div className="modal-footer">
            <button className="modal-ok-btn" type="button" onClick={closeModal}>
              Got it
            </button>
          </div>
        </div>
      </div>

      <div
        className={`modal-backdrop ${activeModal === 'titlealert' ? 'open' : ''}`}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) closeModal();
        }}
      >
        <div className="modal-box" role="dialog" aria-modal="true">
          <div className="modal-letterhead">
            <img
              src={`${process.env.PUBLIC_URL}/letterhead.png`}
              alt="Alachua County Property Appraiser letterhead"
            />
            <button
              className="modal-close"
              type="button"
              onClick={closeModal}
              aria-label="Close Title Alert terms"
            >
              x
            </button>
          </div>
          <div className="modal-body">
            <h3>Terms and Conditions</h3>
            <p>
              The Alachua County Property Appraiser’s Office (ACPA) provides
              property data for tax purposes but does not guarantee its accuracy.
              Official printed documents take precedence. This is not a substitute
              for a title search. Ownership records come from the Clerk of Court’s
              Office. ACPA cannot verify deed validity or settle ownership
              disputes. Suspected fraud should be reported to law enforcement.
            </p>
            <h3>Unsubscribing</h3>
            <p>
              By registering for Title Alert, you consent to receive notifications
              regarding your property. To stop receiving alerts, contact our office
              at 352-744-7744 by phone or text, or by email at info@acpafl.org.
              Please use the same contact information provided during registration.
              If mail was selected as the preferred contact method, opt-out must be
              completed in person at 515 North Main Street, Suite 200, Gainesville,
              FL 32601.
            </p>
            <h3>Confidential Parcels</h3>
            <p>
              If you own a confidential parcel, you must verify your identity in
              person at the Property Appraiser’s Office with a valid driver’s
              license or ID before registering for this service.
            </p>
            <h3>Data Use</h3>
            <p>
              Your contact information is used solely to deliver Title Alert
              notifications and will not be sold or shared with third parties
              outside of applicable Florida public records law.
            </p>
          </div>
          <div className="modal-footer">
            <button className="modal-ok-btn" type="button" onClick={closeModal}>
              I understand
            </button>
          </div>
        </div>
      </div>
    </View>
  );
};

export default Contact;
